import { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ChevronDown, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { V } from "@/lib/vocab";
import { SelfManagedSwitch, selfManagedLabel } from "@/components/SelfManagedSwitch";
import { SetByRedline } from "@/components/SettingsSection";

// Mirrors SETTINGS_ACCESS_DEFAULTS on the server — everything Redline-managed until a super admin opens a dial (iter473).
export const DIAL_DEFAULTS = { assistant: "redline", service: "redline", modules: "redline", advanced: "redline" };
const mgrs = () => `${V.role("hoa_manager")}s`;
export const dialLevelsFactory = () => [
  { v: "redline", label: "Redline only" },
  { v: "company", label: `${V.role("company_admin")}s only` },
  { v: "managers", label: `${mgrs()} and above` },
  { v: "board", label: `${mgrs()} + ${V.grp()}` },
];
const dialSectionsFactory = () => [
  { s: "assistant", label: "Concierge identity & behaviour", desc: `Persona, greetings, guardrails, voice behavior, logo & colours, audience groups, ${V.lc("person")} portal pages, legal & compliance` },
  { s: "service", label: "Channels & capabilities", desc: `Text/voice on-off, capabilities, ${V.lc("person")} portal, dashboard menu` },
  { s: "modules", label: "Modules", desc: "Core concierge, live support, security" },
  { s: "advanced", label: "Advanced", desc: "AI thresholds and system dials — always Redline only", fixed: true },
];

// Redline decides, per community, who can edit Settings: one master "Self-managed" switch that sets the
// three delegable dials at once, the individual dials underneath for exceptions, and the portfolio default.
export function AccessDialCard({ tenant, tenantId, refreshTenants }) {
  const DIAL_LEVELS = dialLevelsFactory();
  const DIAL_SECTIONS = dialSectionsFactory();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [sm, setSm] = useState(null);
  const load = useCallback(() => api.get(`/tenants/${tenantId}/self-managed`).then((r) => setSm(r.data)).catch(() => {}), [tenantId]);
  useEffect(() => { load(); }, [load, tenant?.settings_access]);
  const levelFor = (s) => (tenant?.settings_access || {})[s] || DIAL_DEFAULTS[s];
  const after = async (msg) => { toast.success(msg); await refreshTenants?.(); await load(); };
  const setDial = async (s, v) => {
    if (levelFor(s) === v) return;
    setBusy(s);
    try {
      await api.put(`/tenants/${tenantId}/settings-access`, { section: s, level: v });
      await after(`${DIAL_SECTIONS.find((r) => r.s === s)?.label}: now editable by ${DIAL_LEVELS.find((l) => l.v === v)?.label.toLowerCase()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update access"); }
    finally { setBusy(""); }
  };
  const setSelf = async (v) => {
    setBusy("self");
    try {
      await api.put(`/tenants/${tenantId}/self-managed`, { level: v });
      await after(v === "off" ? "Back to Redline-managed" : `Self-managed by ${selfManagedLabel(v).toLowerCase()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(""); }
  };
  const follow = async () => {
    setBusy("self");
    try {
      await api.put(`/tenants/${tenantId}/self-managed`, { follow_portfolio: true });
      await after(`Now follows ${sm?.portfolio_name || "the portfolio"}'s default`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update"); }
    finally { setBusy(""); }
  };
  const level = sm?.self_managed || "off";
  return (
    <div data-testid="settings-access-card" className="rounded-2xl border border-sand-200 bg-white overflow-hidden">
      <button data-testid="settings-access-toggle" onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-3 p-5 md:px-6 text-left hover:bg-sand-50/60 transition-colors">
        <div className="h-9 w-9 rounded-xl bg-redline-50 flex items-center justify-center shrink-0">
          <ShieldCheck className="h-[18px] w-[18px] text-redline-500" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-[15px] flex items-center gap-2 flex-wrap">Who can edit these settings
            <span data-testid="self-managed-summary" className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${level === "off" ? "bg-sand-100 text-ink/60" : "bg-emerald-100 text-emerald-700"}`}>
              {level === "off" ? "Redline-managed" : `Self-managed · ${selfManagedLabel(level)}`}
            </span>
          </div>
          <div className="text-xs text-muted-foreground truncate">
            {DIAL_SECTIONS.map((r) => `${r.label}: ${DIAL_LEVELS.find((l) => l.v === levelFor(r.s))?.label}`).join(" · ")}
          </div>
        </div>
        <SetByRedline testid="access-dial-set-by" className="shrink-0" />
        <ChevronDown className={`h-4 w-4 text-ink/40 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-sand-100">
          <div className="px-5 md:px-6 py-4 bg-sand-50/60 space-y-3" data-testid="self-managed-row">
            <div>
              <div className="text-sm font-semibold text-ink">Self-managed community</div>
              <div className="text-xs text-muted-foreground">One switch instead of four dials: who may change this {V.lc("org")}'s concierge, channels and modules without asking Redline. Advanced stays with Redline. Every change is in the Activity Log.</div>
            </div>
            <SelfManagedSwitch value={level} onChange={setSelf} busy={busy === "self"} />
            {sm?.portfolio_name && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-ink/70" data-testid="self-managed-portfolio-note">
                <Building2 className="h-3.5 w-3.5 text-ink/40" />
                {sm.source === "community"
                  ? <>Set here for this {V.lc("org")}. {sm.portfolio_name}'s default is <b className="text-ink">{selfManagedLabel(sm.portfolio_default || "off")}</b>.
                      <button type="button" data-testid="self-managed-follow" disabled={busy === "self"} onClick={follow} className="font-semibold text-redline-600 hover:underline disabled:opacity-60">Follow the portfolio default</button></>
                  : <>Follows <b className="text-ink">{sm.portfolio_name}</b>'s portfolio default ({selfManagedLabel(sm.portfolio_default || "off")}). Change it for the whole firm under Portfolio → Settings.</>}
              </div>
            )}
          </div>
          <div className="divide-y divide-sand-100 border-t border-sand-100">
            {DIAL_SECTIONS.map((r) => (
              <div key={r.s} className="px-5 py-3 flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink">{r.label}</div>
                  <div className="text-xs text-muted-foreground">{r.desc}</div>
                </div>
                {r.fixed ? (
                  <span data-testid={`access-${r.s}-fixed`} className="shrink-0 rounded-full bg-ink text-white px-3 py-1 text-xs font-semibold">Redline only</span>
                ) : (
                  <div className="flex flex-wrap rounded-full border border-sand-200 p-0.5 bg-sand-50 shrink-0">
                    {DIAL_LEVELS.map((l) => (
                      <button key={l.v} data-testid={`access-${r.s}-${l.v}`} disabled={busy === r.s} onClick={() => setDial(r.s, l.v)}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors disabled:opacity-60 ${levelFor(r.s) === l.v ? "bg-ink text-white" : "text-ink/60 hover:text-ink"}`}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <p className="px-5 py-2.5 text-[11px] text-muted-foreground">
              <span className="font-semibold">“{mgrs()} and above”</span> means this {V.lc("org")}'s {V.role("hoa_manager").toLowerCase()}s plus the {V.lc("mgmt_co")}'s {V.role("company_admin").toLowerCase()}s. <span className="font-semibold">“{V.role("company_admin")}s only”</span> keeps {V.role("hoa_manager").toLowerCase()}s view-only. <span className="font-semibold">“{mgrs()} + {V.grp()}”</span> adds any {V.lc("board")} you’ve given the per-person <span className="font-semibold">&ldquo;Can configure&rdquo;</span> toggle on the Team page — every other {V.lc("board")} stays read-only.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
